/*
 * Confidential & Proprietary Notice (same as above)
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Card, Modal } from "react-bootstrap";
import { unsubscribePlan } from "../../../subscription/core/_requests";
import { ProfileDetails } from "../../core/_requests";
import { useFormattedUtils } from "../../../../component/dateUtils";

const FREE_PLAN_LIMIT = 10;

const PlanUnsubscribePage: React.FC = () => {
  // Route param must match URL: /subscription/unsubscribe/:id
 const { chefId } = useParams();
console.log("Chef ID from URL:", chefId);
 // use this consistently
const id = chefId;
  const navigate = useNavigate();
  const { formatDateUS } = useFormattedUtils();

  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [showManageDishesModal, setShowManageDishesModal] = useState(false);
  const [showFinalConfirmationModal, setShowFinalConfirmationModal] = useState(false);

  const [listPlan, setListPlan] = useState<any>(null);
  const [listPlanDish, setListPlanDish] = useState<any>(null);

  // Fetch subscription details
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!chefId) {
          toast.error("Invalid subscription link!");
         navigate(`subscription/list/${id}`);
          return;
        }

        const res = await ProfileDetails(id);
        const plan = res?.data?.chef_plan_subscriptions?.[0] || null;
        const dish = res?.data || null;

        setListPlan(plan);
        setListPlanDish(dish);

        // Only show modal if user has active plan
        setShowUnsubscribeModal(true);
      } catch (error) {
        toast.error("Unable to load subscription details");
      } finally {
        setInitializing(false);
      }
    };

    fetchData();
  }, [chefId]);


  // Action Handlers
  const handleConfirmUnsubscribe = () => {
    setShowUnsubscribeModal(false);
    setShowFeaturesModal(true);
  };

  const handleProceedWithFeaturesWarning = () => {
    setShowFeaturesModal(false);

    const totalDishCount = listPlanDish?.totalDishCount ?? 0;

    if (totalDishCount > FREE_PLAN_LIMIT) {
      setShowManageDishesModal(true);
    } else {
      setShowFinalConfirmationModal(true);
    }
  };

  const handleManageDishesOk = () => {
    setShowManageDishesModal(false);
    navigate("/settings/subscription-dish");
  };

  const handleProceedWithUnsubscribe = async () => {
    if (!listPlan) {
      toast.error("No plan information found");
      return;
    }

    const { plan_id, amount, type, payment_id, validity } = listPlan;
    const payload = { planId: plan_id, chefId, amount, type, validity, payment_id };

    try {
      setLoading(true);
      const response = await unsubscribePlan(payload);
      const data = response?.data ?? response;

      if (data?.status) {
        toast.success(data?.message ?? "Unsubscribed successfully");
        navigate(`subscription/list/${id}`);
        setTimeout(() => window.location.reload(), 400);
      } else {
        toast.error(data?.message ?? "Failed to unsubscribe");
      }
    } catch {
      toast.error("Error during unsubscribe process");
    } finally {
      setLoading(false);
    }
  };


  // Loader UI while fetching API
  if (initializing) {
    return (
      <div className="container text-center py-5">
        <h5>Loading subscription details...</h5>
      </div>
    );
  }


  return (
    <div className="container py-4">
      <h4 className="fw-bold mb-3">Manage Subscription</h4>

      {/* Unsubscribe Confirmation Modal */}
      {listPlan && (
        <Modal show={showUnsubscribeModal} centered onHide={() => navigate(-1)}>
          <Modal.Header closeButton>
            <Modal.Title>Unsubscribe from {listPlan?.plan?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to unsubscribe from <b>{listPlan?.plan?.name}</b>?
          </Modal.Body>
          <Modal.Footer className="justify-content-center">
            <Button variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirmUnsubscribe}>Yes, Continue</Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Features Warning Modal */}
      <Modal show={showFeaturesModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠ Before You Proceed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-danger fw-medium">You will lose access to all premium features immediately.</p>

          {/* {listPlan?.plan_end_date && (
            <p>Your plan ends on: <b>{formatDateUS(listPlan?.plan_end_date) || "-"}</b></p>
          )} */}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button onClick={handleProceedWithFeaturesWarning}>Continue</Button>
        </Modal.Footer>
      </Modal>

      {/* Manage Dishes Modal */}
      <Modal show={showManageDishesModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Manage Dishes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You currently have <b>{listPlanDish?.totalDishCount ?? 0}</b> dishes.</p>
          <p>Your free plan allows only <b>{FREE_PLAN_LIMIT}</b>.</p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button onClick={handleManageDishesOk}>Manage Now</Button>
        </Modal.Footer>
      </Modal>

      {/* Final Confirmation Modal */}
      <Modal show={showFinalConfirmationModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Final Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This action cannot be undone. Are you sure you want to unsubscribe?
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button onClick={() => navigate(-1)}>No</Button>
          <Button disabled={loading} onClick={handleProceedWithUnsubscribe}>
            {loading ? "Processing..." : "Yes, Unsubscribe"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PlanUnsubscribePage;
